import {
    AsOfBadge,
    DateTime,
    Money,
    ObjectRef,
    PageHeader,
    StatusBadge,
    statusLabel,
    type ListColumnDef,
} from '@bemor/web-kit/ui';
import { Link } from 'react-router-dom';

import type { StaffSessionRow } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { ADMIN_STATUSES } from '@/lib/statuses';

const PATH = '/staff/sessions';
const PAGE_KEY = 'sessions';

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const sessionsPrefetch = adminListPrefetch(PAGE_KEY, PATH);

const sessionFilters = filterDefs(PATH, {
    state: 'State',
    risk_verdict: 'Risk result',
    merchant_id: 'Merchant',
    attempts: 'Attempts',
    range: 'Created',
});

/** The session page, where the charge page's session link lands (T-41). */
export const sessionHref = (id: string) =>
    `/sessions/${encodeURIComponent(id)}`;

/** A session's customer: the name over the email, a dash when the buyer gave neither yet. */
export function SessionCustomer({
    session: s,
}: {
    session: Pick<StaffSessionRow, 'buyer_name' | 'buyer_email'>;
}) {
    if (!s.buyer_name && !s.buyer_email) return <>—</>;
    return (
        <span className="flex min-w-0 flex-col">
            {s.buyer_name ? <span>{s.buyer_name}</span> : null}
            {s.buyer_email ? (
                <span className="text-muted-foreground text-sm break-all">
                    {s.buyer_email}
                </span>
            ) : null}
        </span>
    );
}

/**
 * bm-111 QA-10: who issued a session: the creator's label (a user's name, an API key's name, a service) with its id
 * beside it; the id alone when the API could not name it.
 */
export function SessionCreator({
    creator,
}: {
    creator: StaffSessionRow['created_by'];
}) {
    return (
        <span className="flex min-w-0 flex-col" data-testid="session-creator">
            {creator.label ? <span>{creator.label}</span> : null}
            <span className="text-muted-foreground font-mono text-xs break-all">
                {creator.id}
            </span>
        </span>
    );
}

/**
 * bm-111 QA-37 (§ 10): a session's type as staff read it, from its mode, source and the subscription a setup session's
 * card replaces; an unknown mode reads as its own words.
 */
export function sessionType(
    s: Pick<
        StaffSessionRow,
        'mode' | 'source' | 'setup_subscription_public_id'
    >,
): string {
    switch (s.mode) {
        case 'one_off':
            return 'One-off payment';
        case 'plan':
            return s.source === 'billing'
                ? 'Subscription renewal'
                : 'Subscription signup';
        case 'setup':
            return s.setup_subscription_public_id ? 'Update card' : 'Add card';
        case 'plan_change':
            return 'Plan change';
        default:
            return statusLabel(s.mode);
    }
}

/**
 * bm-111 QA: how a session was issued, as staff read it (display only; the stored value, contract and API keep the
 * code). `dashboard` is a signed-in merchant's bearer token forwarded by the web-kit app server, never an API key. An
 * unknown source reads as its own words.
 */
const SESSION_SOURCES: Record<string, string> = {
    api: 'Merchant API key',
    dashboard: 'Merchant user token',
    billing: 'Billing renewal',
    console: 'Bemor test console',
    portal: 'Buyer portal or card link',
    system: 'System',
    renewal_auth: 'Renewal authorization',
};

export function sessionSource(source: StaffSessionRow['source']): string {
    return SESSION_SOURCES[source] ?? statusLabel(source);
}

/** A session's risk verdict: the latest attempt's check, a dash before any attempt. */
export function SessionVerdict({ verdict }: { verdict: string | null }) {
    return verdict ? (
        <StatusBadge status={verdict} domain={ADMIN_STATUSES.riskVerdict} />
    ) : (
        <>—</>
    );
}

/**
 * bm-111 § 10: every merchant's checkout sessions, newest first, with state, amount, customer, attempts and risk
 * verdict (§ 9), and how and by whom each was issued (type, source, creator; QA-10). A phone card keeps state, amount
 * and verdict; the rest is on the session page the row links to.
 */
export function SessionsPage() {
    const columns: ListColumnDef<StaffSessionRow>[] = [
        {
            key: 'created_at',
            aliases: ['created'],
            label: 'Created',
            kind: 'datetime',
            sortKey: 'created_at',
            csv: (r) => r.created_at ?? '',
            cell: (r) => <DateTime value={r.created_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'id',
            label: 'Session',
            cell: (r) => (
                <Link
                    to={sessionHref(r.id)}
                    className="font-mono text-xs hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {r.id}
                </Link>
            ),
            csv: (r) => r.id,
            boardCard: true,
            noTruncate: true,
        },
        {
            key: 'merchant_id',
            aliases: ['merchant'],
            label: 'Merchant',
            sortKey: 'merchant_id',
            csv: (r) => r.merchant_id,
            cell: (r) => <ObjectRef id={r.merchant_id} />,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            csv: (r) => r.state,
            boardCard: true,
            cell: (r) => (
                <StatusBadge status={r.state} domain={ADMIN_STATUSES.session} />
            ),
        },
        {
            key: 'amount_cents',
            aliases: ['amount'],
            label: 'Amount',
            csv: (r) => r.amount_cents ?? '',
            boardCard: true,
            cell: (r) =>
                r.amount_cents === null ? (
                    '—'
                ) : (
                    <Money cents={r.amount_cents} currency="USD" />
                ),
            noTruncate: true,
        },
        {
            key: 'customer',
            label: 'Customer',
            csv: (r) => r.buyer_email ?? '',
            cell: (r) => <SessionCustomer session={r} />,
        },
        {
            key: 'mode',
            aliases: ['type'],
            label: 'Type',
            sortKey: 'mode',
            csv: (r) => r.mode,
            cell: (r) => sessionType(r),
        },
        {
            key: 'source',
            label: 'Source',
            csv: (r) => r.source,
            cell: (r) => (
                <span data-testid="session-source">
                    {sessionSource(r.source)}
                </span>
            ),
        },
        {
            key: 'created_by',
            aliases: ['creator'],
            label: 'Created by',
            csv: (r) => r.created_by.id,
            cell: (r) => <SessionCreator creator={r.created_by} />,
        },
        {
            key: 'attempts',
            label: 'Attempts',
            sortKey: 'attempts',
            csv: (r) => r.attempt,
            cell: (r) => (
                <span data-testid="session-attempts">{r.attempt}</span>
            ),
        },
        {
            key: 'risk_verdict',
            label: 'Risk result',
            csv: (r) => r.risk_verdict ?? '',
            boardCard: true,
            cell: (r) => <SessionVerdict verdict={r.risk_verdict} />,
        },
    ];
    const { state, query } = useAdminList<StaffSessionRow>(PAGE_KEY, PATH, {
        columns,
        filters: sessionFilters,
    });
    return (
        <div>
            <PageHeader
                title="Sessions"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            Checkout sessions across merchants, with their
                            attempts and risk result.
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="sessions"
                state={state}
                query={query}
                rowKey={(r) => r.id}
                rowHref={(r) => sessionHref(r.id)}
                mobileCard={{
                    fields: ['state', 'amount_cents', 'risk_verdict'],
                }}
            />
        </div>
    );
}
